<!--
** This component is mostly taken from @RangeDatePicker.vue because the date range selector isn't supported in the
** Nord UI library yet.
** Also I couldn't use the @RangeDatePicker.vue directly because it was messing up the UI.
** And refactoring it would've needed testing as it's used in multiple places.
-->
<template>
    <nord-button
        size="s"
        ref="triggerRef"
        :variant="variant"
        @click="togglePopout"
    >
        <nord-icon size="s" name="interface-calendar" slot="start" />
        {{ displayDateRange || dynamicPlaceHolderText }}
    </nord-button>

    <nord-popout ref="popoutRef" class="date-range-popout">
        <div class="date-range-popout-content">
            <div class="date-range-popout-body">
                <div class="date-range-container">
                    <div class="calendar-container">
                        <input
                            type="text"
                            ref="flatpickrInput"
                            class="flatpickr-input-hidden"
                        />
                    </div>
                </div>
            </div>
        </div>
    </nord-popout>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import Flatpickr from 'flatpickr';
import moment from 'moment';

// Import localization
import { Danish } from 'flatpickr/dist/l10n/da.js';
import { Swedish } from 'flatpickr/dist/l10n/sv.js';
import { Finnish } from 'flatpickr/dist/l10n/fi.js';
import { Norwegian } from 'flatpickr/dist/l10n/no.js';

// Set up locale
if (typeof moment_locale !== 'undefined') {
    switch(moment_locale) {
        case "da": Flatpickr.localize(Danish); break;
        case "sv": Flatpickr.localize(Swedish); break;
        case "fi": Flatpickr.localize(Finnish); break;
        case "no": Flatpickr.localize(Norwegian); break;
    }
}

Flatpickr.l10ns.default.firstDayOfWeek = 1;
Flatpickr.l10ns.default.rangeSeparator = ' to ';

const props = defineProps({
    selectedItem: {
        type: Object,
        default: () => ({})
    },

    placeholderText: {
        type: String,
        default: () => trans('tables.filters.placeholders.selectDate')
    },

    variant: {
        type: String,
        default: 'default'
    },

    format: {
        type: String,
        default: 'YYYY-MM-DD'
    },

    maxRange: {
        type: Object,
        default: () => ({ value: 365, unit: 'days' })
    },

    operator: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['item-selected']);

const triggerRef = ref(null);
const popoutRef = ref(null);
const flatpickrInput = ref(null);
const flatpickr = ref(null);
const selectedDates = ref({});
const pendingSelection = ref({});
const isPopoutVisible = ref(false);

// Determine mode based on operator
const currentMode = computed(() => {
    const rangeOperators = ['between', 'not_between'];

    if (props.operator && rangeOperators.includes(props.operator.value)) {
        return 'range';
    }

    return 'single';
});

const dynamicPlaceHolderText = computed(() => {
    return currentMode.value === 'single' 
        ? trans('tables.filters.placeholders.selectDate') 
        : trans('tables.filters.placeholders.selectDateRange');
})

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return moment(dateStr).locale(moment_locale).format('D MMM YYYY');
};

const displayDateRange = computed(() => {
    if (props.selectedItem?.value) {
        const value = props.selectedItem.value;

        if (currentMode.value === 'single' && value.date) {
            return formatDate(value.date);
        } else if (typeof value === 'string') {
            return formatDate(value);
        } else if (value.start && value.end) {
            if (value.start === value.end) {
                return formatDate(value.start);
            }
            return `${formatDate(value.start)} - ${formatDate(value.end)}`;
        }
    }
    return null;
});

function togglePopout() {
    if (isPopoutVisible.value) {
        hidePopout();
    } else {
        showPopout();
    }
}

function showPopout() {
    if (!popoutRef.value) return;

    popoutRef.value.show();
    isPopoutVisible.value = true;

    const value = props.selectedItem?.value;

    if (value) {
        if (currentMode.value === 'single') {
            pendingSelection.value = {
                date: typeof value === 'string' ? value : value.date ?? null,
            };
        } else if (value.start && value.end) {
            pendingSelection.value = {
                start: value.start,
                end: value.end,
            };
        }
    }

    nextTick(initFlatpickr);
}

function hidePopout() {
    if (popoutRef.value) {
        popoutRef.value.hide();
        isPopoutVisible.value = false;
    }
}

function initFlatpickr() {
    if (!flatpickrInput.value) return;

    if (flatpickr.value) {
        flatpickr.value.destroy();
    }

    const config = {
        mode: currentMode.value,
        dateFormat: 'Y-m-d',
        static: true,
        inline: true,
        showMonths: 1,
        defaultDate: [],
        onChange: handleDateChange,
        onReady: handleFlatpickrReady
    };

    flatpickr.value = new Flatpickr(flatpickrInput.value, config);

    setInitialFlatpickrDate();
}


function handleDateChange(selectedDates) {
    if (currentMode.value === 'single') {
        handleSingleDateChange(selectedDates);
    } else {
        handleRangeDateChange(selectedDates);
    }
}

function handleSingleDateChange(selectedDates) {
    if (selectedDates.length === 1) {
        const date = moment(selectedDates[0]).format(props.format);
        pendingSelection.value = { date };
        applySingleSelection(date);
    } else {
        pendingSelection.value = { date: null };
    }
}

function handleRangeDateChange(selectedDates) {
    if (selectedDates.length === 1) {
        const date = moment(selectedDates[0]);
        if (props.maxRange) {
            flatpickr.value.set('minDate', date.clone().subtract(props.maxRange.value, props.maxRange.unit).format('YYYY-MM-DD'));
            flatpickr.value.set('maxDate', date.clone().add(props.maxRange.value, props.maxRange.unit).format('YYYY-MM-DD'));
        }

        pendingSelection.value = {
            start: date.format(props.format),
            end: null
        };
    } else if (selectedDates.length === 2) {
        const start = moment(selectedDates[0]).format(props.format);
        const end = moment(selectedDates[1]).format(props.format);

        pendingSelection.value = { start, end };

        flatpickr.value.set('minDate', null);
        flatpickr.value.set('maxDate', null);

        applyRangeSelection(start, end);
    } else {
        pendingSelection.value = { start: null, end: null };
    }
}

function handleFlatpickrReady(_, __, instance) {
    instance.calendarContainer.classList.add('date-range-picker');
    instance.calendarContainer.classList.add(currentMode.value === 'range' ? 'rangeMode' : 'singleMode');
}

function setInitialFlatpickrDate() {
    const value = props.selectedItem?.value;
    if (!value) return;

    if (currentMode.value === 'single') {
        const date = typeof value === 'string' ? value : value.date;
        if (date) flatpickr.value.setDate(date);
    } else if (value.start && value.end) {
        flatpickr.value.setDate([value.start, value.end]);
    }
}

function applySingleSelection(date) {
    emit('item-selected', {
        value: date
    });

    selectedDates.value = { date };
    hidePopout();
}

function applyRangeSelection(start, end) {
    emit('item-selected', {
        value: { start, end }
    });

    selectedDates.value = { start, end };
    hidePopout();
}

onBeforeUnmount(() => {
    if (flatpickr.value) {
        flatpickr.value.destroy();
    }
});

watch(() => props.selectedItem, (newValue) => {
    if (!newValue?.value || !flatpickr.value) return;

    const value = newValue.value;
    const isSingle = currentMode.value === 'single';

    if (isSingle) {
        const date = typeof value === 'string' ? value : value.date;
        if (!date) return;

        pendingSelection.value = { date };
        if (isPopoutVisible.value) flatpickr.value.setDate(date);
        return;
    }

    // Range mode
    if (value.start && value.end) {
        pendingSelection.value = { start: value.start, end: value.end };
        if (isPopoutVisible.value) flatpickr.value.setDate([value.start, value.end]);
    }
}, { deep: true });

watch(() => props.operator, () => {
    if (isPopoutVisible.value) {
        nextTick(() => {
            initFlatpickr();
        });
    }
}, { deep: true });
</script>

<style scoped lang="scss">
.flatpickr-input-hidden {
    display: none;
}
</style>
